// Solomon Portal — typed fetch wrapper for `upload-attachment` Edge
// Function (issue #16 frontend half, Day 4).
//
// Contract:
//   POST /functions/v1/upload-attachment
//     multipart/form-data with field `file: File`
//
//   200 OK -> { id: string (uuid v4), filename, mimeType, size }
//   400 -> { error: 'rejected', reason: 'unsupported_mime' | 'too_large'
//                              | 'empty_file' | 'no_file' | 'invalid_form' }
//   401 -> unauthorized
//   403 -> forbidden (deactivated)
//   500 -> upstream / config error
//
// Spec: SPEC-reply-composer.md §2.1 / §3.1.

import { supabase } from './supabase';
import { env } from './env';
import { logger } from './logger';

/** Server-returned descriptor for a staged attachment. The `id` is the
 *  uuid the SPA passes to post-reply / post-conversation when sending. */
export interface StagedAttachment {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
}

/** Reason codes mirrored from the Edge Function's typed rejection. */
export type UploadRejectionReason =
  | 'unsupported_mime'
  | 'too_large'
  | 'empty_file'
  | 'no_file'
  | 'invalid_form';

export type UploadAttachmentResult =
  | { kind: 'ok'; attachment: StagedAttachment }
  | { kind: 'rejected'; reason: UploadRejectionReason }
  | { kind: 'unauthenticated' }
  | { kind: 'forbidden' }
  | { kind: 'config_error' }
  | { kind: 'network_error' }
  | { kind: 'aborted' };

/** Cap matches the Edge Function's MAX_FILE_SIZE_BYTES (10 MB). Keeping
 *  it client-side too lets us short-circuit and surface a friendlier
 *  error before kicking off a 10 MB upload. */
export const CLIENT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/** Optimistic client-side MIME allowlist mirror. Server still re-checks. */
export const CLIENT_ALLOWED_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
]);

export interface UploadAttachmentOpts {
  file: File;
  signal?: AbortSignal;
}

/** Upload a single staged attachment. Returns a tagged-union outcome
 *  so callers can switch on `result.kind` instead of try/catching. The
 *  function never throws.
 */
export async function uploadAttachment(
  opts: UploadAttachmentOpts,
): Promise<UploadAttachmentResult> {
  // Client-side fast-fail for the obvious cases. Server still re-checks.
  if (opts.file.size === 0) {
    return { kind: 'rejected', reason: 'empty_file' };
  }
  if (opts.file.size > CLIENT_MAX_FILE_SIZE_BYTES) {
    return { kind: 'rejected', reason: 'too_large' };
  }
  if (!CLIENT_ALLOWED_MIME_TYPES.has(opts.file.type)) {
    return { kind: 'rejected', reason: 'unsupported_mime' };
  }

  let session;
  try {
    const { data } = await supabase.auth.getSession();
    session = data.session;
  } catch (err: unknown) {
    logger.error({
      source: 'upload-attachment-api.getSession',
      message: 'getSession threw',
      payload: { error: String(err) },
    });
    return { kind: 'network_error' };
  }
  if (!session) return { kind: 'unauthenticated' };

  const fd = new FormData();
  fd.append('file', opts.file, opts.file.name);

  let res: Response;
  try {
    res = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/upload-attachment`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}` },
      body: fd,
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    });
  } catch (err: unknown) {
    if (opts.signal?.aborted) return { kind: 'aborted' };
    logger.error({
      source: 'upload-attachment-api.fetch',
      message: 'fetch threw',
      payload: { error: String(err) },
    });
    return { kind: 'network_error' };
  }

  if (res.status === 401) return { kind: 'unauthenticated' };
  if (res.status === 403) return { kind: 'forbidden' };

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { kind: 'config_error' };
  }

  if (res.status === 400) {
    const reason = (body as { reason?: string })?.reason;
    if (
      reason === 'unsupported_mime' ||
      reason === 'too_large' ||
      reason === 'empty_file' ||
      reason === 'no_file' ||
      reason === 'invalid_form'
    ) {
      return { kind: 'rejected', reason };
    }
    return { kind: 'config_error' };
  }

  if (res.status !== 200) {
    return { kind: 'config_error' };
  }

  const obj = body as Partial<StagedAttachment>;
  if (
    typeof obj.id !== 'string' ||
    typeof obj.filename !== 'string' ||
    typeof obj.mimeType !== 'string' ||
    typeof obj.size !== 'number'
  ) {
    logger.error({
      source: 'upload-attachment-api.fetch',
      message: 'response shape invalid',
      payload: { keys: Object.keys(obj) },
    });
    return { kind: 'config_error' };
  }

  return {
    kind: 'ok',
    attachment: {
      id: obj.id,
      filename: obj.filename,
      mimeType: obj.mimeType,
      size: obj.size,
    },
  };
}
